
import { useState, useEffect, useCallback } from 'react'
import { libraryAPI } from '@/lib/api/library'
import type { LibraryEntryWithModel } from '@genieartinc/schemas'

export interface LibraryModel {
  modelId: string
  modelName: string
  brandSlug: string | null
  brandName: string | null
  purpose: string | null
  sortOrder: number
  /** Set when the catalog knows where the model runs. Absent means the list is not split. */
  route?: 'local' | 'cloud'
}

export interface UseLibraryModelsResult {
  data: LibraryModel[]
  isLoading: boolean
  isEmpty: boolean
  error: Error | null
  refetch: () => void
}

// Module-level cache keyed by nodeType with TTL
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

interface CacheEntry<T> {
  data: T
  timestamp: number
}

const libraryCache = new Map<string, CacheEntry<LibraryModel[]>>()

function getCached(key: string): LibraryModel[] | null {
  const entry = libraryCache.get(key)
  if (!entry) return null
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    libraryCache.delete(key)
    return null
  }
  return entry.data
}

function setCache(key: string, data: LibraryModel[]): void {
  libraryCache.set(key, { data, timestamp: Date.now() })
}

function getCacheKey(nodeType: string, purpose?: string): string {
  return purpose ? `${nodeType}:${purpose}` : nodeType
}

function mapEntryToLibraryModel(entry: LibraryEntryWithModel): LibraryModel {
  return {
    modelId: entry.model.id,
    modelName: entry.model.name,
    brandSlug: entry.model.modelFamilySlug ?? null,
    brandName: entry.model.modelFamilyName ?? null,
    purpose: entry.model.purpose ?? null,
    sortOrder: entry.sortOrder,
  }
}

/**
 * Warm the library models cache without triggering React state updates.
 * Call from non-component code (e.g., prefetch utilities).
 */
export async function warmLibraryModelsCache(
  nodeType: string
): Promise<LibraryModel[]> {
  const cacheKey = getCacheKey(nodeType)
  const cached = getCached(cacheKey)
  if (cached) return cached

  try {
    const entries = await libraryAPI.getLibrary({ nodeType, limit: 100 })
    const models = entries.map(mapEntryToLibraryModel)
    setCache(cacheKey, models)
    return models
  } catch {
    return []
  }
}

/**
 * Fetch library models for a given node type, optionally filtered by purpose.
 * Returns a flat list with brand info for grouping in the UI.
 */
export function useLibraryModels(
  nodeType: string,
  purpose?: string
): UseLibraryModelsResult {
  // Cache key includes purpose so different purpose views don't share stale data
  const cacheKey = getCacheKey(nodeType, purpose)
  // Base cache key for fetching all models of this nodeType (API doesn't filter by purpose)
  const baseCacheKey = getCacheKey(nodeType)

  const [allModels, setAllModels] = useState<LibraryModel[]>(() => {
    return getCached(baseCacheKey) || []
  })
  const [isLoading, setIsLoading] = useState(() => {
    return getCached(baseCacheKey) === null
  })
  const [error, setError] = useState<Error | null>(null)

  const fetchModels = useCallback(async () => {
    const cached = getCached(baseCacheKey)
    if (cached) {
      setAllModels(cached)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const entries = await libraryAPI.getLibrary({ nodeType, limit: 100 })
      const models = entries.map(mapEntryToLibraryModel)
      setCache(baseCacheKey, models)
      setAllModels(models)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
      setAllModels([])
    } finally {
      setIsLoading(false)
    }
  }, [nodeType, baseCacheKey])

  useEffect(() => {
    fetchModels()
  }, [fetchModels])

  // Client-side purpose filter (library is small, <50 models)
  const data = purpose
    ? allModels.filter((m) => m.purpose === purpose)
    : allModels

  return {
    data,
    isLoading,
    isEmpty: !isLoading && data.length === 0,
    error,
    refetch: fetchModels,
  }
}
